import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useSessionLanguage } from '@context/SessionSettingsContext'
import { useSnackbar } from '@context/SnackbarContext'

import { Heading4 } from '@ui/Typography'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { TwoFAProviderEnum } from '@model/TwoFactorAuthentication'

import { handleBaseTwoFactorErrors } from '../Profile/AccountSettings/handleBaseTwoFactorErrors'
import { TwoFactorAuthEvents } from '../TwoFactorAuth/useTwoFactorAuth'
import { ResetPasswordFormComplete } from './ResetPasswordFormComplete'

import styles from './ForgotPasswordPage.module.scss'

export interface ResetPasswordCompleteFormValues {
  email: string
  confirmPassword: string
  password: string
  token: string
}

export interface ResetPasswordFormValues {
  confirmPassword: string
  password: string
}

export const ResetPasswordPage: React.FC = () => {
  const { email, token } = useParams<{ email: string; token: string }>()
  const navigate = useNavigate()
  const locale = useSessionLanguage()

  const { t } = useTranslation()

  const apiClient = useApiClient(ClientApiClient)
  const { addSnackbar } = useSnackbar()

  useEffect(() => {
    if (!email || !token) {
      navigate('/login')
    } else {
      window.history.replaceState({}, '', `/${locale}/users/reset-password`)
    }
  }, [email, locale, navigate, token])

  const handleSubmit = async (values: ResetPasswordFormValues) => {
    if (email && token) {
      const decodedEmail = decodeURIComponent(email)

      try {
        const response = await apiClient.resetPasswordComplete({
          password: values.password,
          email: decodedEmail,
          token,
        })

        const hasTwoFactor = (response?.twoFactor?.length ?? 0) > 0

        if (hasTwoFactor) {
          const googleAuthProvider = response.twoFactor?.find(
            (twoFactor) => twoFactor.provider?.id === TwoFAProviderEnum.GoogleAuthenticator
          )

          const authMedium = googleAuthProvider
            ? t('Profile.Google Authenticator app')
            : response.twoFactor?.[0]?.authMedium

          navigate('/2fa/confirm', {
            replace: true,
            state: {
              twoFactorAuthData: {
                twoFactorAuthEvent: TwoFactorAuthEvents.ResetPassword,
                email: decodedEmail,
                password: values.password,
                urlToken: token,
                twoFactor: googleAuthProvider
                  ? [{ ...response.twoFactor?.[0], authMedium }]
                  : response.twoFactor,
              },
            },
          })
        } else {
          addSnackbar.success({ message: t('Password has been reset successfully!') })
          navigate('/login')
        }
      } catch (error: unknown) {
        const isErrorCaught = handleBaseTwoFactorErrors(error, navigate)
        if (!isErrorCaught) {
          throw error
        }
      }
    }
  }

  if (!email || !token) {
    return null
  }

  return (
    <React.Fragment>
      <div className={styles.topWrapper}>
        <div className={styles.wrapper}>
          <div className={styles.inner}>
            <Heading4 className={styles.passwordChangeTitle}>
              {t('Profile.Password Change')}
            </Heading4>
            <ResetPasswordFormComplete email={email} token={token} onSubmit={handleSubmit} />
          </div>
        </div>
      </div>
    </React.Fragment>
  )
}
